import React, { useEffect, useRef, useState } from 'react'
import { Modal } from 'semantic-ui-react'
import { X } from 'phosphor-react'
import styled from 'styled-components'
import { FlexRowBetween, LoaderBar } from 'components'
import { AppButton, AppPrimaryButton } from 'elements'
import { MobileQrService } from 'utility'
import { useNotification } from 'hooks/toast'
import { QrPreviewBox } from 'page-components/report-approval/report-approval-styles'

const QrModal = styled(Modal)`
    &&&.ui.modal {
        width: auto !important;
        max-width: 560px !important;
        margin: 0 auto !important;
    }

    &&&.ui.modal > .content {
        padding: 1.25rem 1.5rem !important;
    }

    &&&.ui.modal > .actions {
        display: flex !important;
        flex-wrap: nowrap;
        flex-direction: row;
        justify-content: flex-end;
        align-items: center;
        gap: 8px;
        padding: 1rem 1.25rem !important;

        .button {
            margin: 0 !important;
            flex: 0 0 auto;
            white-space: nowrap;
        }
    }

    @media (max-width: 767px) {
        &&&.ui.modal {
            width: 96% !important;
            max-width: none !important;
        }

        &&&.ui.modal > .content {
            padding: 1rem !important;
        }

        &&&.ui.modal > .actions {
            flex-wrap: wrap;
            flex-direction: column-reverse;
            align-items: stretch;

            .button {
                width: 100% !important;
                white-space: normal;
            }
        }
    }
`

export const QrStickerModal = ({
    open,
    onClose,
    templateId,
    templateName = '',
    customerId = null,
}) => {
    const { errorToast, successToast } = useNotification()
    const errorToastRef = useRef(errorToast)
    errorToastRef.current = errorToast

    const [previewUrl, setPreviewUrl] = useState('')
    const [loadingPreview, setLoadingPreview] = useState(false)
    const [downloading, setDownloading] = useState('')
    const previewRequestIdRef = useRef(0)

    useEffect(() => {
        if (!open) {
            setPreviewUrl(prev => {
                if (prev) URL.revokeObjectURL(prev)
                return ''
            })
            setDownloading('')
            setLoadingPreview(false)
            previewRequestIdRef.current += 1
        }
    }, [open])

    useEffect(() => {
        if (!open || !templateId || !customerId) {
            return undefined
        }

        const requestId = previewRequestIdRef.current + 1
        previewRequestIdRef.current = requestId

        const loadPreview = async () => {
            try {
                setLoadingPreview(true)
                // Preview: GET /Mobile/QrCode?format=1&size=256
                const blob = await MobileQrService.fetchQrCodeBlob({
                    TemplateId: templateId,
                    CustomerId: customerId,
                    format: 1,
                    size: 256,
                })
                if (previewRequestIdRef.current !== requestId) return

                const objectUrl = URL.createObjectURL(blob)
                setPreviewUrl(prev => {
                    if (prev) URL.revokeObjectURL(prev)
                    return objectUrl
                })
            } catch {
                if (previewRequestIdRef.current === requestId) {
                    errorToastRef.current(
                        'Failed to load QR preview. Please try again.',
                    )
                    setPreviewUrl(prev => {
                        if (prev) URL.revokeObjectURL(prev)
                        return ''
                    })
                }
            } finally {
                if (previewRequestIdRef.current === requestId) {
                    setLoadingPreview(false)
                }
            }
        }

        loadPreview()

        return () => {
            if (previewRequestIdRef.current === requestId) {
                previewRequestIdRef.current += 1
            }
        }
    }, [open, templateId, customerId])

    const handleDownload = async (shape, label) => {
        if (!templateId || !customerId) {
            errorToast('Unable to generate sticker. Please try again.')
            return
        }

        try {
            setDownloading(shape)
            const safeName = (templateName || 'template')
                .replace(/[^\w-]+/g, '_')
                .slice(0, 40)
            // format=2&shape=square | format=2&shape=circle
            await MobileQrService.downloadQrCode(
                {
                    TemplateId: templateId,
                    CustomerId: customerId,
                    format: 2,
                    shape,
                },
                `${safeName}-qr-${shape}.png`,
            )
            successToast(`${label} downloaded`)
        } catch {
            errorToast(`Failed to download ${label.toLowerCase()}.`)
        } finally {
            setDownloading('')
        }
    }

    const canDownload = !!templateId && !!customerId && !downloading

    return (
        <QrModal
            open={open}
            onClose={onClose}
            size="tiny"
            closeOnDimmerClick={false}
        >
            <Modal.Header>
                <FlexRowBetween>
                    <span>Generate QR Sticker</span>
                    <X className="pointer" size={24} onClick={onClose} />
                </FlexRowBetween>
            </Modal.Header>
            <Modal.Content>
                <p style={{ marginTop: 0, color: '#6b7280', fontSize: 13 }}>
                    {templateName
                        ? `Template: ${templateName}`
                        : 'Preview and download a scannable inspection QR sticker.'}
                </p>

                <QrPreviewBox>
                    {loadingPreview && (
                        <LoaderBar content="Loading preview…" />
                    )}
                    {!loadingPreview && previewUrl && (
                        <img src={previewUrl} alt="QR preview" />
                    )}
                    {!loadingPreview && !previewUrl && (
                        <span className="qr-preview-caption">
                            {customerId
                                ? 'Preview unavailable'
                                : 'No customer selected for QR generation'}
                        </span>
                    )}
                    <p className="qr-preview-caption">
                        Scan to start inspection
                    </p>
                </QrPreviewBox>
            </Modal.Content>
            <Modal.Actions>
                <AppButton type="button" onClick={onClose}>
                    Close
                </AppButton>
                <AppPrimaryButton
                    type="button"
                    disabled={!canDownload}
                    onClick={() =>
                        handleDownload('square', 'Download square sticker')
                    }
                >
                    {downloading === 'square'
                        ? 'Downloading…'
                        : 'Download square sticker'}
                </AppPrimaryButton>
                <AppPrimaryButton
                    type="button"
                    disabled={!canDownload}
                    onClick={() =>
                        handleDownload('circle', 'Download round sticker')
                    }
                >
                    {downloading === 'circle'
                        ? 'Downloading…'
                        : 'Download round sticker'}
                </AppPrimaryButton>
            </Modal.Actions>
        </QrModal>
    )
}
